import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DnsOutlined,
  PhoneInTalkOutlined,
  SettingsEthernetOutlined,
  FingerprintOutlined,
  DialpadOutlined,
  RestartAltOutlined,
  DeleteOutlineOutlined,
} from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import SettingsCard from "../Settings/SettingsCard";

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    width: "100%",
  },
  helper: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },
  columns: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: theme.spacing(3),
    alignItems: "start",
  },
  column: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  row: {
    display: "flex",
    gap: theme.spacing(1.5),
  },
  groupTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: theme.palette.text.disabled,
    marginTop: theme.spacing(1),
    "&::after": {
      content: '""',
      flex: 1,
      height: 1,
      background: theme.palette.divider,
    },
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(1),
  },
  restartBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
  routesList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  routeRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1.25),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    fontFamily: "ui-monospace, Consolas, monospace",
    fontSize: "0.86rem",
  },
  empty: {
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
    fontStyle: "italic",
  },
}));

const iconAdornment = (Icon) => ({
  startAdornment: (
    <InputAdornment position="start">
      <Icon fontSize="small" color="disabled" />
    </InputAdornment>
  ),
});

const AsteriskControlSettings = () => {
  const { classes } = useStyles();

  const [trunkIp, setTrunkIp] = useState("");
  const [trunkPort, setTrunkPort] = useState("");
  const [openaiProjectId, setOpenaiProjectId] = useState("");
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [removingRoute, setRemovingRoute] = useState(null);

  const cargarConfig = async () => {
    try {
      const { data } = await api.get("/asterisk/config");
      setTrunkIp(data.trunkIp || "");
      setTrunkPort(data.trunkPort != null ? String(data.trunkPort) : "");
      setOpenaiProjectId(data.openaiProjectId || "");
    } catch (err) {
      toastError(err);
    }
  };

  const cargarRoutes = async () => {
    try {
      const { data } = await api.get("/asterisk/routes");
      setRoutes(data.routes || []);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([cargarConfig(), cargarRoutes()]);
      setLoading(false);
    })();
  }, []);

  const guardar = async () => {
    if (saving) return;
    setSaving(true);

    try {
      const { data } = await api.put("/asterisk/config", {
        trunkIp: trunkIp.trim(),
        trunkPort: Number(trunkPort),
        openaiProjectId: openaiProjectId.trim(),
      });

      if (data.reloadError) {
        toast.error(
          `Se guardó, pero el reload en vivo falló: ${data.reloadError}. Probá "Reiniciar servicio".`
        );
      } else {
        toast.success("Configuración guardada y aplicada en caliente.");
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const quitarRuta = async (numero) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`¿Quitar la ruta ${numero}? La conexión que la usa dejará de recibir llamadas.`)) {
      return;
    }

    setRemovingRoute(numero);
    try {
      const { data } = await api.delete(`/asterisk/routes/${numero}`);
      setRoutes(data.routes || []);
      toast.success(`Ruta ${numero} eliminada.`);
    } catch (err) {
      toastError(err);
    } finally {
      setRemovingRoute(null);
    }
  };

  const reiniciar = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Esto corta cualquier llamada activa por un momento. ¿Reiniciar el servicio ahora?")) {
      return;
    }

    setRestarting(true);
    try {
      await api.post("/asterisk/restart");
      toast.success("Servicio reiniciado.");
    } catch (err) {
      toastError(err);
    } finally {
      setRestarting(false);
    }
  };

  return (
    <SettingsCard
      icon={PhoneInTalkOutlined}
      title="Central telefónica"
      subtitle="La dirección de tu central telefónica y la cuenta de OpenAI son compartidas por todas las líneas. Guardar aplica en caliente, sin cortar el servicio."
    >
    <Box className={classes.root}>
      <div className={classes.columns}>
        <div className={classes.column}>
          <div className={classes.groupTitle}>
            <SettingsEthernetOutlined fontSize="small" /> Troncal SIP
          </div>
          <div className={classes.row}>
            <TextField
              label="IP de tu troncal SIP"
              size="small"
              variant="outlined"
              fullWidth
              placeholder="159.89.181.191"
              helperText="La dirección de tu central telefónica -- de ahí es de donde llegan las llamadas."
              value={trunkIp}
              onChange={(e) => setTrunkIp(e.target.value)}
              InputProps={iconAdornment(DnsOutlined)}
            />
            <TextField
              label="Puerto"
              size="small"
              variant="outlined"
              style={{ maxWidth: 140, flexShrink: 0 }}
              placeholder="5060"
              value={trunkPort}
              onChange={(e) => setTrunkPort(e.target.value.replace(/\D/g, ""))}
            />
          </div>

          <div className={classes.groupTitle}>
            <FingerprintOutlined fontSize="small" /> Cuenta de OpenAI
          </div>
          <TextField
            label="ID de la cuenta de OpenAI"
            size="small"
            variant="outlined"
            fullWidth
            placeholder="proj_XXXXXXXXXXXXXXXX"
            helperText="A qué cuenta de OpenAI se conectan las llamadas -- lo sacás de Settings → Project → General en OpenAI."
            value={openaiProjectId}
            onChange={(e) => setOpenaiProjectId(e.target.value)}
            InputProps={iconAdornment(FingerprintOutlined)}
          />

          <div className={classes.actions}>
            <Button variant="contained" color="primary" disabled={loading || saving} onClick={guardar}>
              {saving ? "Guardando…" : "Guardar cambios"}
            </Button>
          </div>
        </div>

        <div className={classes.column}>
          <div className={classes.groupTitle}>
            <DialpadOutlined fontSize="small" /> Rutas activas hacia la IA
          </div>
          <Typography className={classes.helper} style={{ marginTop: -4 }}>
            Una por cada conexión SIP creada. Para agregar una nueva, creala desde{" "}
            <strong>Conexiones → Llamada por teléfono → pestaña Proveedor Telefónico</strong>.
          </Typography>

          <div className={classes.routesList}>
            {routes.length === 0 && !loading ? (
              <Typography className={classes.empty}>Todavía no hay ninguna ruta configurada.</Typography>
            ) : (
              routes.map((numero) => (
                <div key={numero} className={classes.routeRow}>
                  <Chip size="small" label={numero} variant="outlined" />
                  <Tooltip title="Quitar esta ruta">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={removingRoute === numero}
                        onClick={() => quitarRuta(numero)}
                      >
                        <DeleteOutlineOutlined fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <Divider />

      <Box className={classes.restartBox}>
        <div>
          <Typography style={{ fontWeight: 600, fontSize: "0.86rem" }}>Reiniciar servicio</Typography>
          <Typography className={classes.helper}>
            Equivalente al reinicio manual por SSH. Corta llamadas activas por un momento.
          </Typography>
        </div>
        <Button
          variant="outlined"
          color="error"
          startIcon={<RestartAltOutlined fontSize="small" />}
          disabled={restarting}
          onClick={reiniciar}
        >
          {restarting ? "Reiniciando…" : "Reiniciar servicio"}
        </Button>
      </Box>
    </Box>
    </SettingsCard>
  );
};

export default AsteriskControlSettings;
